import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type {
    AddServiceInput,
    UpdateServiceMutationInput,
    UpdateServiceRatesInput,
} from '../model/service-catalog.types';
import { serviceCatalogApi } from './service-catalog.api';
import { serviceCatalogOptions } from './service-catalog.queries';

// NSS-528 disposition #2: returning the invalidation means `isPending` and a caller's own
// `onSuccess` both wait for the refetched list, so a sheet closes only once the row is there.
function invalidateServiceCatalog(queryClient: ReturnType<typeof useQueryClient>) {
    return queryClient.invalidateQueries({ queryKey: serviceCatalogOptions().queryKey });
}

export function useAddServiceOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (data: AddServiceInput) => serviceCatalogApi.addService(data),
        onSuccess: () => invalidateServiceCatalog(queryClient),
        onError: () => toast.error('Failed to add service'),
    });
}

export function useUpdateServiceOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: ({ code, ...data }: UpdateServiceMutationInput) =>
            serviceCatalogApi.updateService(code, data),
        onSuccess: () => invalidateServiceCatalog(queryClient),
        onError: () => toast.error('Failed to update service'),
    });
}

export function useDeleteServiceOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (code: string) => serviceCatalogApi.deleteService(code),
        onSuccess: () => invalidateServiceCatalog(queryClient),
        onError: () => toast.error('Failed to delete service'),
    });
}

export function useSetDefaultServiceOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (code: string) => serviceCatalogApi.setDefaultService(code),
        onSuccess: () => invalidateServiceCatalog(queryClient),
        onError: () => toast.error('Failed to set default service'),
    });
}

export function useUpdateServiceRatesOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (data: UpdateServiceRatesInput) => serviceCatalogApi.updateRates(data),
        onSuccess: () => invalidateServiceCatalog(queryClient),
        onError: () => toast.error('Failed to update rates'),
    });
}
